import axiosInstance from 'config/AxiosConfig';
import { ProjectUserInviteReq } from 'domain/payload/request/clientTransmora/projectUser/ProjectUserInviteReq';
import { ProjectUserUpdateReq } from 'domain/payload/request/clientTransmora/projectUser/ProjectUserUpdateReq';
import { BaseResponse } from 'domain/payload/response/BaseResponseDto';
import { ProjectUserDetailResp } from 'domain/payload/response/clientTransmora/projectUser/ProjectUserDetailResp';
import { ProjectUserListResp } from 'domain/payload/response/clientTransmora/projectUser/ProjectUserListResp';
import { ProjectUserConfigResp } from 'domain/payload/response/clientTransmora/projectUser/ProjectUserConfigResp';
import { UserRoleResp } from 'domain/payload/response/user/UserRoleResp';

const ProjectUserService = () => {
  const projectUserList = async (
    token: string,
    search: string | null,
    page: number | null,
    size: number | null
  ): Promise<BaseResponse<ProjectUserListResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/client-project-user/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          search: search,
          page: page,
          size: size
        }
      });

      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message || 'Failed to fetch project user list';
    }
  };

  const projectUserConfig = async (
    token: string
  ): Promise<BaseResponse<ProjectUserConfigResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/client-project-user/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message || 'Failed to fetch project user config';
    }
  };

  const projectUserDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<ProjectUserDetailResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/client-project-user/detail?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message || 'Failed to fetch project user detail';
    }
  };

  const projectUserInvite = async (
    token: string,
    request: ProjectUserInviteReq
  ): Promise<BaseResponse<ProjectUserDetailResp>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/client-project-user/invite`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message || 'Failed to invite project user';
    }
  };

  const projectUserUpdate = async (
    token: string,
    request: ProjectUserUpdateReq
  ): Promise<BaseResponse<ProjectUserDetailResp>> => {
    try {
      const response = await axiosInstance.put(`/v1/legacy/cms/client-project-user/update`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message || 'Failed to update project user';
    }
  };

  const projectUserDelete = async (
    token: string,
    id: string
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.delete(`/v1/legacy/cms/client-project-user/delete?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message || 'Failed to delete project user';
    }
  };

  const projectUserRoleConfig = async (
    token: string
  ): Promise<BaseResponse<UserRoleResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/client-project-user/role-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch roles';
    }
  };

  const projectUserClientConfig = async (
    token: string
  ): Promise<BaseResponse<UserRoleResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/client-project-user/client-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch clients';
    }
  };

  return {
    projectUserList,
    projectUserConfig,
    projectUserDetail,
    projectUserInvite,
    projectUserUpdate,
    projectUserDelete,
    projectUserRoleConfig,
    projectUserClientConfig,
  };
};

export default ProjectUserService;